<template>
  <PainelLayout>
    <Breadcrumb v-if="!isOpeto" class="d-print-none" :lista="[
      { 'name':'Painel', 'link' : '/' }]"
    />

    <div class="row align-items-center d-print-none">
      <div class="col-md-6">
        <h1>Roteiro</h1>
      </div>
      <div class="col-md-6 d-flex flex-wrap gap-2 justify-content-md-end">
        <RentalSearch />
        <button class="btn btn-outline-secondary" @click="imprimir" :disabled="!paradasVisiveis.length && !atrasadas.length">
          <i class="bi bi-printer me-2"></i>Imprimir / PDF
        </button>
      </div>
    </div>
    <hr class="d-print-none"/>

    <!-- Filtros -->
    <form class="row g-2 align-items-end mb-3 d-print-none" @submit.prevent="carregar">
      <div class="col-6 col-md-3">
        <label class="form-label" for="dtInicio">De</label>
        <input v-model="periodo.start" required type="date" class="form-control" id="dtInicio">
      </div>
      <div class="col-6 col-md-3">
        <label class="form-label" for="dtFim">Até</label>
        <input v-model="periodo.end" :min="periodo.start" required type="date" class="form-control" id="dtFim">
      </div>
      <div class="col-12 col-md-6 d-flex flex-wrap gap-2">
        <button type="button" class="btn btn-outline-primary" @click="atalho(0)">Hoje</button>
        <button type="button" class="btn btn-outline-primary" @click="atalho(1)">Amanhã</button>
        <button type="button" class="btn btn-outline-primary" @click="atalho(0, 6)">7 dias</button>
        <button type="submit" class="btn btn-primary">Buscar</button>
      </div>
      <div class="col-12 d-flex flex-wrap gap-3">
        <div class="form-check form-switch">
          <input v-model="mostrarConcluidas" class="form-check-input" type="checkbox" role="switch" id="chkConcluidas">
          <label class="form-check-label" for="chkConcluidas">Mostrar concluídas</label>
        </div>
        <div class="form-check form-switch">
          <input v-model="mostrarBalcao" class="form-check-input" type="checkbox" role="switch" id="chkBalcao">
          <label class="form-check-label" for="chkBalcao">Mostrar balcão (retirada/devolução)</label>
        </div>
      </div>
    </form>

    <div v-if="loading" class="d-flex justify-content-center p-3">
      <div class="spinner-border" style="width: 4rem; height: 4rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else class="route-print-area">
      <h2 class="d-none d-print-block h4 mb-3">Roteiro — {{ tituloPeriodo }}</h2>

      <!-- Atrasadas: sempre visíveis, independentes do período -->
      <section v-if="atrasadas.length" class="mb-4">
        <h3 class="h5 text-danger mb-2"><i class="bi bi-exclamation-triangle me-2"></i>Atrasadas ({{ atrasadas.length }})</h3>
        <StopCard v-for="p in atrasadas" :key="'a' + p.type + p.rental_id" :stop="p" overdue :show-values="!isOpeto" />
      </section>

      <p v-if="!paradasVisiveis.length" class="text-muted">Nenhuma entrega, coleta ou movimentação no balcão no período.</p>

      <section v-for="dia in dias" :key="dia.date" class="mb-4 route-day">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h3 class="h5 mb-0">{{ formatarDia(dia.date) }}</h3>
          <div class="d-print-none">
            <span class="text-muted small me-2">
              {{ dia.stops.length }} parada(s)<template v-if="!isOpeto"> · a cobrar {{ money(dia.aCobrar) }}</template>
            </span>
            <a v-if="rotaDoDia(dia)" :href="rotaDoDia(dia)" target="_blank" rel="noopener" class="btn btn-sm btn-outline-success">
              <i class="bi bi-signpost-split me-1"></i>Rota no mapa
            </a>
          </div>
        </div>

        <StopCard v-for="(p, i) in dia.stops" :key="p.type + p.rental_id" :stop="p" :index="i + 1" :show-values="!isOpeto" />
      </section>
    </div>
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import { onMounted, ref, reactive, computed } from 'vue';
import { deliveryRoute } from '@/models/deliveryRoute';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import RentalSearch from '@/components/RentalSearch.vue';
import StopCard, { destinoParada } from './StopCard.vue';
import { usePermissions } from '@/composables/usePermissions';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';

// Data local no formato YYYY-MM-DD (toISOString usaria UTC)
function dataLocal(offsetDias = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return d.toLocaleDateString('sv-SE');
}

export default {
  name: "RouteView",
  components: { PainelLayout, Breadcrumb, RentalSearch, StopCard },
  setup() {

    const toastStore = useToastStore();
    const { isOpeto } = usePermissions();
    const model = deliveryRoute();
    const loading = ref(false);
    const paradas = ref([]);
    const atrasadas = ref([]);
    const base = ref(null);
    const mostrarConcluidas = ref(false);
    const mostrarBalcao = ref(true);
    const periodo = reactive({ start: dataLocal(), end: dataLocal() });

    const paradasVisiveis = computed(() =>
      paradas.value.filter(p => (mostrarConcluidas.value || !p.done) && (mostrarBalcao.value || !p.counter))
    );

    const dias = computed(() => {
      const grupos = {};
      paradasVisiveis.value.forEach(p => {
        grupos[p.date] = grupos[p.date] || { date: p.date, stops: [], aCobrar: 0 };
        grupos[p.date].stops.push(p);
        grupos[p.date].aCobrar += Number(p.amount_due || 0);
      });
      return Object.values(grupos).sort((a, b) => a.date.localeCompare(b.date));
    });

    const tituloPeriodo = computed(() =>
      periodo.start === periodo.end ? formatarData(periodo.start) : `${formatarData(periodo.start)} a ${formatarData(periodo.end)}`
    );

    async function carregar() {
      loading.value = true;
      try {
        const res = await model.list(periodo.start, periodo.end);
        paradas.value = res.stops;
        atrasadas.value = res.overdue ?? [];
        base.value = res.base;
      } catch (error) {
        toastStore.error('Erro ao carregar roteiro: ' + parseError(error));
      }
      loading.value = false;
    }

    function atalho(offset, dias = 0) {
      periodo.start = dataLocal(offset);
      periodo.end = dataLocal(offset + dias);
      carregar();
    }

    // Rota com todas as entregas/coletas pendentes do dia (balcão fica de fora), na ordem de horário.
    // O Google Maps aceita até 9 pontos intermediários na URL.
    function rotaDoDia(dia) {
      const pontos = dia.stops.filter(p => !p.done && destinoParada(p)).map(destinoParada).slice(0, 10);
      if (!pontos.length) return null;
      const params = new URLSearchParams({ api: '1', travelmode: 'driving', destination: pontos[pontos.length - 1] });
      if (base.value) params.set('origin', `${base.value.lat},${base.value.lng}`);
      if (pontos.length > 1) params.set('waypoints', pontos.slice(0, -1).join('|'));
      return 'https://www.google.com/maps/dir/?' + params.toString();
    }

    function imprimir() {
      window.print();
    }

    function formatarData(iso) {
      return iso ? iso.split('-').reverse().join('/') : '';
    }

    function formatarDia(iso) {
      const [y, m, d] = iso.split('-').map(Number);
      const texto = new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' });
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    }

    function money(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    onMounted(carregar);

    return {
      loading, periodo, mostrarConcluidas, mostrarBalcao, paradasVisiveis, atrasadas, dias, tituloPeriodo, isOpeto,
      carregar, atalho, rotaDoDia, imprimir, formatarData, formatarDia, money
    }
  }
}
</script>

<!-- Impressão: só a área do roteiro aparece (o painel tem menu/navbar globais) -->
<style>
@media print {
  body * {
    visibility: hidden;
  }

  .route-print-area,
  .route-print-area * {
    visibility: visible;
  }

  .route-print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }

  .route-print-area .route-stop {
    break-inside: avoid;
    border: 1px solid #999 !important;
  }

  .route-print-area .route-done {
    opacity: 1;
  }
}
</style>
